import React, { useEffect, useCallback, useRef } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler } from 'chart.js';
import { Line } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';
import annotationPlugin from 'chartjs-plugin-annotation';
import SplitScreenContainer from '../common/SplitScreenContainer';
import ReportChooser from './ReportChooser';
import ChartControls from './components/ChartControls';
import chartSyncActivator from '../../utils/ChartSyncActivator';
import SplitScreenReportLogic from './utils/SplitScreenReportLogic';

// Хуки
import { useChartState } from './hooks/useChartState';
import { useChartEvents } from './hooks/useChartEvents';
import { useChartEnhancements } from './hooks/useChartEnhancements';
import { useChartRegistration } from './hooks/useChartRegistration';

// Утилиты
import { createCustomTooltip, createChartOptions, mergeChartOptions } from './utils/chartConfig';
import './ChartStyles.css';

// Регистрируем необходимые компоненты ChartJS
ChartJS.register(
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  Title, 
  Tooltip, 
  Legend, 
  Filler,
  zoomPlugin,
  annotationPlugin
);

// Активируем синхронизацию графиков при импорте
if (!chartSyncActivator.initialized) {
  chartSyncActivator.initialize();
}

// Глобальные переменные
if (typeof window.reportChooserModalOpen === 'undefined') {
  window.reportChooserModalOpen = false;
}

if (typeof window.selectedChartPoint === 'undefined') {
  window.selectedChartPoint = {
    timestamp: null,
    pointIndex: null,
    value: null,
    containerId: null
  };
}

const BaseChart = ({ 
  title,
  vehicle,
  startDate,
  endDate,
  data,
  labels,
  yAxisLabel,
  color = 'rgb(75, 192, 192)',
  backgroundGradient = true,
  fetchData,
  formatTooltipLabel = (value) => `${value}`,
  formatYAxisLabel = (value) => `${value}`,
  formatXAxisLabel = (value) => value,
  emptyDataMessage = 'Нет данных для отображения',
  reportType,
  options: customOptions
}) => {
  // Используем кастомные хуки
  const {
    expandedMode,
    isLoading,
    chartData,
    error,
    showKeyboardShortcuts,
    showReportChooser,
    setShowReportChooser,
    setSplitDirection,
    containerToFill,
    setContainerToFill,
    syncGroupId,
    setSyncGroupId,
    localStorageVehicle,
    localStorageDateRange,
    chartRef,
    chartContainerRef,
    containerId,
    loadData,
    debouncedLoadData,
    toggleExpandedMode,
    toggleKeyboardShortcuts,
    getContainerClasses,
    handleChartError
  } = useChartState(
    title,
    vehicle,
    startDate,
    endDate,
    data,
    labels,
    color,
    backgroundGradient,
    fetchData
  );

  // Функция для обработки выделения (объявляем до useChartEvents)
  const handleSelection = useCallback((startIndex, endIndex) => {
    console.log(`BaseChart: Выделение с ${startIndex} по ${endIndex}`);
    // Здесь можно добавить логику обработки выделения
  }, []);

  // Функция сброса зума (объявляем до useChartEvents)
  const resetZoom = useCallback(() => {
    console.log(`BaseChart: Сброс масштабирования в контейнере ${containerId}`);
    
    if (!chartRef.current) {
      console.warn(`BaseChart: Не удалось найти ссылку на график для сброса масштаба`);
      return;
    }
    
    try {
      const isResetting = chartRef.current._isResettingZoom;
      
      if (isResetting) {
        console.log(`BaseChart: Сброс масштаба уже выполняется, пропускаем`);
        return;
      }
      
      chartRef.current._isResettingZoom = true;
      
      if (chartRef.current.resetZoom) {
        console.log(`BaseChart: Вызываем resetZoom графика`);
        chartRef.current.resetZoom();
      }
      
      if (chartRef.current.canvas) {
        chartRef.current.canvas.removeAttribute('data-selection-applied');
      }
      
      if (window.chartSyncManager && window.chartSyncManager.syncEnabled) {
        console.log(`BaseChart: Синхронизируем сброс масштаба через менеджер`);
        window.chartSyncManager.resetAllZoom(containerId);
      }
      
      setTimeout(() => {
        if (chartRef.current) {
          chartRef.current._isResettingZoom = false;
        }
      }, 200);
      
    } catch (error) {
      console.error(`BaseChart: Ошибка при сбросе масштаба:`, error);
      
      if (chartRef.current) {
        chartRef.current._isResettingZoom = false;
      }
    }
  }, [chartRef, containerId]);

  // Используем хук событий
  const {
    exportChart,
    toggleSyncMode,
    handleChartClick,
    getDataPoints,
    handleSelection: enhancedHandleSelection,
    notifySelectionChanged: enhancedNotifySelectionChanged
  } = useChartEvents(
    chartRef,
    chartContainerRef,
    containerId,
    reportType,
    handleSelection,
    syncGroupId,
    resetZoom
  );

  // Используем хук улучшений
  const {
    getDebugInfo,
    exportChartState,
    forceNotifySelection,
    analyzePerformance
  } = useChartEnhancements(
    chartRef,
    containerId,
    syncGroupId,
    localStorageVehicle,
    localStorageDateRange,
    getDataPoints,
    enhancedNotifySelectionChanged
  );

  // Используем хук регистрации графика
  useChartRegistration(
    chartRef,
    chartContainerRef,
    containerId,
    syncGroupId,
    setSyncGroupId
  );

  // Инициализируем логику разделения экрана (объявляем здесь, чтобы использовать в useEffect ниже)
  const splitLogicRef = useRef(null);

  // Обработчик события создания отчета
  useEffect(() => {
    const handleReportCreated = (event) => {
      const { reportContainer, reportType } = event.detail || {};
      
      console.log(`BaseChart: Получено событие createReport`, event.detail);
      
      // Если отчет создан в нашем контейнере или в соседнем, принудительно регистрируем графики
      if (reportContainer === containerId || 
          (reportContainer && reportContainer.includes(containerId.split('-')[0]))) {
        
        console.log(`BaseChart: Запускаем принудительную регистрацию графиков после создания отчета ${reportType} в контейнере ${reportContainer}`);
        
        setTimeout(() => {
          // Принудительное сканирование графиков через ChartSyncManager
          if (window.chartSyncManager && window.chartSyncManager.scanForNewCharts) {
            console.log(`BaseChart: Запускаем агрессивное сканирование ChartSyncManager`);
            window.chartSyncManager.scanForNewCharts(true);
          }
          
          // Дополнительно запускаем нашу логику принудительной регистрации
          if (splitLogicRef.current && splitLogicRef.current.forceScanAndRegisterCharts) {
            splitLogicRef.current.forceScanAndRegisterCharts();
          }
          
          // Проверяем состояние регистрации нашего графика
          if (chartRef.current && window.chartSyncManager) {
            const isRegistered = window.chartSyncManager.charts.has(containerId);
            console.log(`BaseChart: График ${containerId} ${isRegistered ? 'зарегистрирован' : 'НЕ зарегистрирован'} в ChartSyncManager`);
            
            if (!isRegistered) {
              console.log(`BaseChart: Принудительная регистрация графика ${containerId}`);
              window.chartSyncManager.registerChart(containerId, chartRef.current);
            }
          }
        }, 500);
      }
    };
    
    // Обработчик события готовности графика к синхронизации
    const handleChartSyncReady = (event) => {
      const { totalCharts, newCharts, charts } = event.detail || {};
      console.log(`BaseChart: Получено событие chartSyncReady`, { totalCharts, newCharts, charts, myContainerId: containerId });
      
      // Если наш график не в списке зарегистрированных, пытаемся зарегистрировать
      if (charts && !charts.includes(containerId) && chartRef.current) {
        console.log(`BaseChart: Наш график ${containerId} отсутствует в списке зарегистрированных, принудительная регистрация`);
        
        setTimeout(() => {
          if (window.chartSyncManager && chartRef.current && !chartRef.current.isDestroyed) {
            window.chartSyncManager.registerChart(containerId, chartRef.current);
          }
        }, 100);
      }
    };
    
    document.addEventListener('createReport', handleReportCreated);
    document.addEventListener('chartSyncReady', handleChartSyncReady);
    
    return () => {
      document.removeEventListener('createReport', handleReportCreated);
      document.removeEventListener('chartSyncReady', handleChartSyncReady);
    };
  }, [containerId, chartRef, splitLogicRef]);

  // Инициализируем логику разделения экрана
  useEffect(() => {
    // Создаем экземпляр логики разделения экрана
    splitLogicRef.current = new SplitScreenReportLogic(
      containerId,
      chartContainerRef,
      setShowReportChooser,
      setContainerToFill,
      setSplitDirection,
      showReportChooser
    );
    
    // Подписываемся на события
    splitLogicRef.current.attachEventListeners();
    
    // Очистка при размонтировании
    return () => {
      if (splitLogicRef.current) {
        splitLogicRef.current.destroy();
        splitLogicRef.current = null;
      }
    };
  }, [containerId, setShowReportChooser, setContainerToFill, setSplitDirection]);

  // Обновляем showReportChooser в splitLogic при его изменении
  useEffect(() => {
    if (splitLogicRef.current) {
      splitLogicRef.current.updateShowReportChooser(showReportChooser);
    }
  }, [showReportChooser]);

  // Debug режим для development
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`BaseChart: Инициализация debug режима для ${containerId}`, {
        hasChartRef: !!chartRef.current,
        hasChartSyncManager: !!window.chartSyncManager,
        syncGroupId
      });
      
      window.chartDebug = {
        ...window.chartDebug,
        [containerId]: {
          chartRef,
          containerId,
          syncGroupId,
          loadData,
          debouncedLoadData,
          handleChartError,
          getContainerClasses,
          getDebugInfo,
          exportChartState,
          forceNotifySelection,
          analyzePerformance
        }
      };
      
      // Проверяем состояние синхронизации каждые 30 секунд (уменьшаем частоту логов)
      const debugInterval = setInterval(() => {
        if (window.chartSyncManager) {
          const syncStatus = {
            registeredCharts: window.chartSyncManager.charts.size,
            syncEnabled: window.chartSyncManager.syncEnabled,
            hasChart: !!chartRef.current,
            containerId,
            suppressEvents: window.chartSyncManager.suppressEvents
          };
          
          console.log(`BaseChart Debug ${containerId}:`, syncStatus);
          
          // Проверяем, зарегистрирован ли наш график
          if (chartRef.current && !window.chartSyncManager.charts.has(containerId)) {
            console.warn(`BaseChart Debug ${containerId}: График не зарегистрирован в менеджере! Попытка повторной регистрации...`);
            
            // Принудительная регистрация
            if (window.chartSyncManager.registerChart) {
              window.chartSyncManager.registerChart(containerId, chartRef.current);
            }
          }
          
          // Расширенная диагностика - проверяем все canvas на странице
          const allCanvases = document.querySelectorAll('canvas');
          const chartCanvases = Array.from(allCanvases).filter(canvas => 
            canvas.hasAttribute('data-graph') || 
            canvas.closest('.chart-content') ||
            canvas.__chartjs__ ||
            canvas.__chartInstance
          );
          
          const registeredChartIds = Array.from(window.chartSyncManager.charts.keys());
          
          console.log(`BaseChart Debug ${containerId}: Всего canvas: ${allCanvases.length}, графических canvas: ${chartCanvases.length}, зарегистрированных: ${registeredChartIds.length}`, {
            registeredIds: registeredChartIds,
            canvasIds: chartCanvases.map(c => c.getAttribute('data-container-id') || 'no-id').filter(Boolean)
          });
          
          // Проверяем незарегистрированные графики
          chartCanvases.forEach(canvas => {
            const canvasContainerId = canvas.getAttribute('data-container-id');
            const chartInstance = canvas.__chartjs__ || canvas.__chartInstance;
            
            if (canvasContainerId && chartInstance && !window.chartSyncManager.charts.has(canvasContainerId)) {
              console.warn(`BaseChart Debug: Найден незарегистрированный график ${canvasContainerId}, регистрируем...`);
              if (!chartInstance.isDestroyed) {
                window.chartSyncManager.registerChart(canvasContainerId, chartInstance);
              }
            }
          });
        }
      }, 30000);
      
      return () => clearInterval(debugInterval);
    }
  }, [chartRef, containerId, syncGroupId, loadData, debouncedLoadData, handleChartError, getContainerClasses, 
      getDebugInfo, exportChartState, forceNotifySelection, analyzePerformance]);

  // Создаем кастомную подсказку
  const customTooltip = createCustomTooltip(formatTooltipLabel, reportType);

  // Создаем опции графика
  const baseChartOptions = createChartOptions(
    yAxisLabel,
    formatYAxisLabel,
    formatXAxisLabel,
    customTooltip,
    containerId,
    enhancedHandleSelection,
    enhancedNotifySelectionChanged
  );

  // Объединяем базовые опции с пользовательскими опциями
  const chartOptions = customOptions ? 
    mergeChartOptions(baseChartOptions, customOptions) : 
    baseChartOptions;

  // Обработчик обновления данных
  const handleRefresh = useCallback(() => {
    loadData();
  }, [loadData]);

  // Функции для обработки разделения экрана и выбора отчетов
  const handleHorizontalSplit = useCallback(() => {
    if (splitLogicRef.current) {
      splitLogicRef.current.handleHorizontalSplit();
    }
  }, []);

  const handleVerticalSplit = useCallback(() => {
    if (splitLogicRef.current) {
      splitLogicRef.current.handleVerticalSplit();
    }
  }, []);

  const handleShowReportChooser = useCallback(() => {
    console.log('BaseChart: Ручной вызов ReportChooser');
    
    // Закрываем существующий если открыт
    if (showReportChooser) {
      setShowReportChooser(false);
      window.reportChooserModalOpen = false;
    }
    
    setTimeout(() => {
      setShowReportChooser(true);
      window.reportChooserModalOpen = containerId;
      console.log(`BaseChart: ReportChooser открыт вручную для контейнера ${containerId}`);
    }, 100);
  }, [containerId, showReportChooser, setShowReportChooser]);

  const handleReportSelect = useCallback((selectedReportType) => {
    if (splitLogicRef.current) {
      splitLogicRef.current.handleReportSelect(
        selectedReportType, 
        containerToFill, 
        vehicle, 
        startDate, 
        endDate,
        null, // splitDirection - получим из состояния компонента
        containerId
      );
    }
  }, [containerToFill, vehicle, startDate, endDate, containerId]);

  const handleReportChooserClose = useCallback(() => {
    if (splitLogicRef.current) {
      splitLogicRef.current.handleReportChooserClose();
    }
  }, []);

  // Эффект для установки ID контейнера
  useEffect(() => {
    console.log(`BaseChart: Инициализация контейнера с ID ${containerId}`);
    
    if (chartContainerRef.current) {
      const container = chartContainerRef.current.closest('.chart-split-container');
      if (container) {
        if (!container.id || container.id !== containerId) {
          console.log(`BaseChart: Устанавливаем ID ${containerId} на DOM-элемент`);
          container.id = containerId;
        }
      }
    }
  }, [containerId, chartContainerRef]);

  // Отрисовка содержимого графика
  const renderChartContent = () => {
    return (
      <>
        <ChartControls
          title={title}
          vehicle={vehicle}
          expandedMode={expandedMode}
          showKeyboardShortcuts={showKeyboardShortcuts}
          onRefresh={handleRefresh}
          onToggleExpanded={toggleExpandedMode}
          onToggleKeyboardShortcuts={toggleKeyboardShortcuts}
          onResetZoom={resetZoom}
          onExportChart={exportChart}
          onHorizontalSplit={handleHorizontalSplit}
          onVerticalSplit={handleVerticalSplit}
          onToggleSyncMode={toggleSyncMode}
          onShowReportChooser={handleShowReportChooser}
        />
        
        <div 
          className="chart-content" 
          ref={chartContainerRef}
          style={{
            width: '100%',
            height: 'calc(100% - 50px)',
            position: 'relative', 
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {isLoading ? (
            <div className="chart-loading">
              <div className="chart-spinner"></div>
              <div>Загрузка данных...</div>
            </div>
          ) : error ? (
            <div className="chart-error">
              <div>{error}</div>
              <button onClick={handleRefresh}>Попробовать снова</button>
            </div>
          ) : chartData && (
            (chartData.labels && chartData.labels.length > 0) ||
            (chartData.datasets && chartData.datasets.length > 0 && 
             chartData.datasets.some(dataset => dataset.data && dataset.data.length > 0))
          ) ? (
            <div style={{ width: '100%', height: '100%', position: 'relative' }}>
              <Line
                data={chartData}
                options={chartOptions}
                ref={chartRef}
                style={{ width: '100%', height: '100%' }}
              />
            </div>
          ) : (
            <div className="chart-empty">
              <div>{emptyDataMessage}</div>
              <button onClick={handleRefresh}>Обновить</button>
            </div>
          )}
        </div>
      </>
    );
  };

  // Добавляем обработку изменения размера для всех графиков
  useEffect(() => {
    const handleChartResize = () => {
      if (chartRef.current) {
        // Проверяем, находится ли график в разделенном контейнере
        const splitPane = chartRef.current.canvas?.closest('.split-pane');
        if (splitPane) {
          // Для разделенных контейнеров добавляем небольшую задержку
          setTimeout(() => {
            if (chartRef.current && !chartRef.current.isDestroyed) {
              chartRef.current.resize();
              chartRef.current.update('none'); // Обновление без анимации для производительности
            }
          }, 100);
        } else {
          // Для обычных контейнеров обновляем сразу
          if (chartRef.current && !chartRef.current.isDestroyed) {
            chartRef.current.resize();
            chartRef.current.update('resize');
          }
        }
      }
    };

    // Добавляем слушатель событий изменения размера
    window.addEventListener('resize', handleChartResize);
    
    // Слушаем специальное событие для разделенных контейнеров
    const handleSplitResize = (event) => {
      if (event.detail && event.detail.containerId === containerId) {
        handleChartResize();
      }
    };
    
    document.addEventListener('splitContainerResized', handleSplitResize);
    
    return () => {
      window.removeEventListener('resize', handleChartResize);
      document.removeEventListener('splitContainerResized', handleSplitResize);
    };
  }, [chartRef, containerId]);

  return (
    <div 
      className={getContainerClasses()} 
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
      onClick={handleChartClick}
      role="img"
      aria-label={`График ${title} для ${vehicle?.name || 'неизвестного транспорта'}`}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleChartClick(e);
        }
      }}
    >
      <SplitScreenContainer 
        id={containerId}
        data-container-id={containerId}
        className="chart-split-container"
        showControls={true}
        allowSplit={true}
        onSplitChange={(splitInfo) => {
          console.log('BaseChart: Получено событие разделения экрана', splitInfo);
          setTimeout(() => {
            if (chartRef.current && chartRef.current.chart) {
              chartRef.current.resize();
              chartRef.current.update();
            }
          }, 200);
        }}
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {renderChartContent()}
      </SplitScreenContainer>
      
      {showReportChooser ? (
        <ReportChooser
          onSelectReport={handleReportSelect}
          onClose={handleReportChooserClose}
          selectedVehicle={vehicle}
          originalReport={reportType}
          containerId={containerId}
        />
      ) : null}
    </div>
  );
};

export default BaseChart; 